/**
 * Theme Toggle for Auth Pages
 *
 * A compact theme toggle button that cycles through light/dark/system modes.
 * Reuses the existing ThemeProvider context.
 */

import { useTranslation } from 'react-i18next';
import { Sun, Moon, Monitor, Check } from 'lucide-react';
import { useTheme } from '@/components/theme/ThemeProvider';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';

export function AuthThemeToggle() {
  const { t } = useTranslation('common');
  const { theme, resolvedTheme, setTheme } = useTheme();

  const icon =
    theme === 'system' ? (
      <Monitor className="h-4 w-4" />
    ) : resolvedTheme === 'dark' ? (
      <Moon className="h-4 w-4" />
    ) : (
      <Sun className="h-4 w-4" />
    );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="h-9 w-9 rounded-xl"
          aria-label={t('theme.toggle')}
        >
          {icon}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="min-w-[140px]">
        <DropdownMenuItem
          onClick={() => setTheme('light')}
          className="flex items-center justify-between gap-2"
        >
          <span className="inline-flex items-center gap-2">
            <Sun className="h-4 w-4" />
            {t('theme.light')}
          </span>
          {theme === 'light' && <Check className="h-4 w-4" />}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => setTheme('dark')}
          className="flex items-center justify-between gap-2"
        >
          <span className="inline-flex items-center gap-2">
            <Moon className="h-4 w-4" />
            {t('theme.dark')}
          </span>
          {theme === 'dark' && <Check className="h-4 w-4" />}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => setTheme('system')}
          className="flex items-center justify-between gap-2"
        >
          <span className="inline-flex items-center gap-2">
            <Monitor className="h-4 w-4" />
            {t('theme.system')}
          </span>
          {theme === 'system' && <Check className="h-4 w-4" />}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
